import React, { useState, useEffect } from 'react';
import { Calendar, Eye, FileText, Clock, CheckCircle, AlertCircle, Search, Filter } from 'lucide-react';
import { problemDiagnosisService } from '../services/problemDiagnosisService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface DiagnosisSession {
  id: string;
  problem_description: string;
  selected_system: string;
  status: 'active' | 'completed' | 'abandoned';
  created_at: string;
  updated_at: string;
  diagnosis_reports?: {
    id: string;
    title: string;
    core_conclusion: string;
    confidence_score?: number;
    created_at: string;
  }[];
}

interface DiagnosisHistoryProps {
  onViewDetail: (sessionId: string) => void;
}

const DiagnosisHistory: React.FC<DiagnosisHistoryProps> = ({ onViewDetail }) => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<DiagnosisSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (user) {
      fetchSessions();
    }
  }, [user, currentPage, statusFilter]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await problemDiagnosisService.getHistory({
        page: currentPage,
        limit: 10,
        status: statusFilter || undefined
      });
      
      setSessions(response.sessions || []);
      setTotalPages(Math.ceil((response.total_count || 0) / 10));
    } catch (error) {
      console.error('获取诊断历史失败:', error);
      toast.error('获取诊断历史失败');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'active':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'abandoned':
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return '已完成';
      case 'active':
        return '进行中';
      case 'abandoned':
        return '已放弃';
      default:
        return '未知';
    }
  };

  const getSystemName = (systemType: string) => {
    const systemNames = {
      machine: '机械系统',
      organism: '生理系统',
      social: '社会系统',
      ecosystem: '生态系统'
    };
    return systemNames[systemType as keyof typeof systemNames] || systemType;
  };

  const filteredSessions = sessions.filter(session =>
    session.problem_description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">诊断历史记录</h2>
        <p className="text-gray-600">查看您之前的问题诊断记录和报告</p>
      </div>

      {/* 搜索和筛选 */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索问题描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">全部状态</option>
              <option value="completed">已完成</option>
              <option value="active">进行中</option>
              <option value="abandoned">已放弃</option>
            </select>
          </div>
        </div>
      </div>

      {/* 诊断记录列表 */}
      <div className="space-y-4">
        {filteredSessions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">暂无诊断记录</h3>
            <p className="text-gray-600">开始您的第一次问题诊断吧！</p>
          </div>
        ) : (
          filteredSessions.map((session) => (
            <div key={session.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusIcon(session.status)}
                    <span className="text-sm font-medium text-gray-600">
                      {getStatusText(session.status)}
                    </span>
                    <span className="text-sm text-gray-400">•</span>
                    <span className="text-sm text-gray-600">
                      {getSystemName(session.selected_system)}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {session.problem_description}
                  </h3>
                  
                  {session.diagnosis_reports && session.diagnosis_reports.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {session.diagnosis_reports[0].core_conclusion}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="w-4 h-4 mr-1" />
                    创建于 {new Date(session.created_at).toLocaleString('zh-CN')}
                  </div>
                </div>
                
                <div className="ml-4">
                  <button
                    onClick={() => onViewDetail(session.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    查看详情
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              第 {currentPage} 页，共 {totalPages} 页
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                上一页
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagnosisHistory;
